import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Crown, Check, Sparkles, Loader2, AlertCircle, 
  ArrowRight, Home, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const pollPaymentStatus = async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/premium/checkout/status/${sessionId}`, {
        withCredentials: true
      });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(response.data);
        
        // Celebrate!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        toast.success('Bienvenue dans l\'univers Premium !');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else if (pollCount < 10) {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Status check error:', err);
      if (pollCount < 5) {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('error');
      }
    }
  };
  
  useEffect(() => {
    pollPaymentStatus();
  }, [sessionId]);
  
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="h-12 w-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-600">Vérification de votre paiement...</p>
          <p className="text-sm text-gray-400 mt-2">Cela peut prendre quelques secondes</p>
        </motion.div>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl border border-red-200 shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Problème de paiement
          </h1>
          
          <p className="text-gray-600 mb-6">
            Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, 
            contactez notre support.
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Réessayer
            </Button>
            
            <Button
              onClick={() => navigate('/premium')}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Retour aux offres
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-indigo-50 flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full"
      >
        {/* Success Card */}
        <div className="bg-white rounded-3xl border border-purple-200 shadow-2xl shadow-purple-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
            >
              <Crown className="h-10 w-10 text-purple-600" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-2xl font-bold text-white mb-2">
                Bienvenue dans l'univers Premium !
              </h1>
              <p className="text-purple-100">
                Votre abonnement est maintenant actif
              </p>
            </motion.div>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="space-y-4 mb-8"
            >
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <Check className="h-5 w-5 text-green-500" />
                <span className="text-green-700">Paiement confirmé</span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                <Sparkles className="h-5 w-5 text-purple-500" />
                <span className="text-purple-700">Accès Premium activé</span>
              </div>
              
              {paymentInfo && (
                <div className="text-center text-sm text-gray-500 pt-2">
                  Montant: {paymentInfo.amount?.toFixed(2)}€
                </div>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-3"
            >
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3 rounded-xl"
                data-testid="go-home-btn"
              >
                <Home className="h-5 w-5 mr-2" />
                Explorer REVELIAE
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              
              <p className="text-center text-xs text-gray-400">
                Un email de confirmation vous a été envoyé
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;
